import React, { useMemo, useState } from 'react';
import KickImage from 'Reuseables/KickImage';

/** CSS import */
import Styles from './Home.module.scss';

/* Hooks import */
import { useFetchSettingData } from './Hooks/useFetchSettingData';

/** Other components import */
import Spacer from './Components/Atoms/Spacer';

/* Section components import */
import TrendingSection from './Components/Organisms/TrendingSection';
import CardCollectionSection from './Components/Organisms/CardCollectionSection';
import ProductCollectionSection from './Components/Organisms/ProductCollectionSection';
import CategorySection from './Components/Organisms/CategorySection';
import OneRowCollectionSection from './Components/Organisms/OneRowCollectionSection';
import HighlightedProductSection from './Components/Organisms/HighlightedProductSection';
import WhatsGoingOnSection from './Components/Organisms/WhatsGoingOnSection';

/** Mocks data import */
import CategoryMockData from './Components/Organisms/CategorySection/MockData.ignore';

/** Helper utilities import */
import { useFetchInstagramFeedData } from './Hooks/useFetchInstagramFeedData';
import SubCategorySection from './Components/Organisms/SubCategorySection';
import { useGetViewType } from './Hooks/useGetViewType';
import { useScrollMemory } from 'helpers/dom';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import BannerSection from './Components/Organisms/BannerSection';
import ModalUnverifiedUser from './Components/Organisms/ModalUnverifiedUser';
import moment from 'moment';
import { useFetchMetadata } from './Hooks/useFetchMetadata';
import { Helmet } from 'react-helmet';

const Home: React.FC = ({ countdown_modal, user_currency, user }: any) => {
  const { isMobileView } = useGetViewType();
  const location = useLocation();

  const [isShowModal, setIsShowModal] = useState(false);

  useScrollMemory();
  const { metaTitle, metaDescription, fetchMetadata } =
    useFetchMetadata('home');

  const { data: homeSettingData, fetchData: fetchDataSection } =
    useFetchSettingData('homepage_all_category_section');
  const { data: preVerifiedSettingData, fetchData: fetchDataPreverified } =
    useFetchSettingData('pre_verified_collection');

  React.useEffect(() => {
    fetchDataPreverified();
    fetchDataSection();
    fetchMetadata();

    if (user.roles.includes('unverified_user')) {
      const unverifiedModal = localStorage.getItem('unverifiedModal');
      if (unverifiedModal) {
        const diff = moment().diff(moment(unverifiedModal), 'days');
        if (diff > 0) {
          setIsShowModal(true);
        }
      } else {
        setIsShowModal(true);
      }
      localStorage.setItem('unverifiedModal', moment().toString());
    }
  }, [location.pathname]);

  const { data: whatsGoingOnData } = useFetchInstagramFeedData();

  const renderHomepageSection = useMemo(() => {
    return homeSettingData.map((item, index) => {
      if (item.isTrending) {
        return (
          <div
            key={`trending-section-${index}`}
            id={`trending-section-${index}`}
          >
            <TrendingSection
              className={Styles['product-collection-section-container']}
              productRowClassname={Styles['product-collection-section']}
              rowClassName={Styles['product-collection-section-row']}
            />
          </div>
        );
      }

      if (item.isSubCategory) {
        return (
          <div
            key={`sub-category-section-${index}`}
            id={`sub-category-section-${index}`}
          >
            <SubCategorySection />
          </div>
        );
      }

      if (item.isPreVerified) {
        return (
          <div
            key={`one-row-collection-section-${index}`}
            id={`one-row-collection-section-${index}`}
          >
            <OneRowCollectionSection
              key={`one-row-collection-section-${index}`}
              slug={item.slug}
              category={item.category}
              title={item.title}
              subtitle={item.subtitle}
              actionText={item.actionText}
              isPreverifiedCollection={
                item.slug &&
                !!preVerifiedSettingData.find((t) => t.slug === item.slug)
              }
              userCurrency={user_currency}
            />
          </div>
        );
      }

      if (item.isHighlightedProduct) {
        return (
          <div
            key={`highlighted-section-${index}`}
            id={`highlighted-section-${index}`}
          >
            <HighlightedProductSection />
          </div>
        );
      }

      if (item.isCategory) {
        return (
          <div
            className={Styles['category-section-container']}
            key={`category-section-${index}`}
            id={`category-section-${index}`}
          >
            <CategorySection
              items={CategoryMockData}
              className={Styles['category-section-row']}
              slug={item.slug}
              category={item.category}
            />
          </div>
        );
      }

      if (item.isInstagramFeeds) {
        return (
          <div
            key={`whats-going-on-section-${index}`}
            id={`whats-going-on-section-${index}`}
          >
            <WhatsGoingOnSection
              items={
                isMobileView ? whatsGoingOnData.slice(0, 6) : whatsGoingOnData
              }
            />
          </div>
        );
      }

      if (item.isHomepageCollection) {
        if (item.type === 'product') {
          return (
            <div
              key={`product-collection-section-${index}`}
              id={`product-collection-section-${index}`}
            >
              <ProductCollectionSection
                title={item.title}
                subtitle={item.subtitle}
                slug={item.slug}
                category={item.category}
                className={Styles['product-collection-section-container']}
                productRowClassname={Styles['product-collection-section']}
                rowClassName={Styles['product-collection-section-row']}
                columnCount={isMobileView ? 5 : 10}
                userCurrency={user_currency}
              />
            </div>
          );
        }

        if (item.type === 'card') {
          return (
            <div
              key={`card-collection-section-${index}`}
              id={`card-collection-section-${index}`}
            >
              <CardCollectionSection
                title={item.title}
                subtitle={item.subtitle}
                category={item.category}
                slug={item.slug}
                className={Styles['card-collection-section-container']}
                productRowClassname={Styles['card-collection-section']}
                rowClassName={Styles['card-collection-section-row']}
                isPreverified={
                  !!preVerifiedSettingData.find((t) => t.slug === item.slug)
                }
                userCurrency={user_currency}
              />
            </div>
          );
        }
      }

      return (
        <div
          key={`undefined-section-${index}`}
          id={`undefined-section-${index}`}
        />
      );
    });
  }, [homeSettingData, whatsGoingOnData]);

  return (
    <div
      className={Styles['wrapper']}
      style={{
        marginTop: countdown_modal
          ? isMobileView || window.document.body.clientWidth < 1019
            ? 144
            : 82
          : isMobileView || window.document.body.clientWidth < 1019
          ? 112
          : 50,
      }}
    >
      {metaTitle !== '' && metaDescription !== '' && (
        <Helmet>
          <title>{metaTitle}</title>
          <meta
            name="description"
            content={metaDescription}
            data-react-helmet="true"
          />
        </Helmet>
      )}
      <BannerSection key={`banner-section-`} isMobileView={isMobileView} />
      {renderHomepageSection}
      <Spacer height={isMobileView ? 0 : 32} />
      <ModalUnverifiedUser
        isHome
        isShow={isShowModal}
        setIsShow={setIsShowModal}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  countdown_modal: state.dataMenu.countdown_modal,
  user_currency: state.dataAuth.userCurrency,
  user: state.dataAuth.userdata,
});

export default connect(mapStateToProps, null)(Home);
